import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import axios from "axios";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

// ============================================================
// TYPES
// ============================================================

export type EventRegistrantType =
  | "member"
  | "contact"
  | "non_member";

export type EventVisibility =
  | "public"
  | "private"
  | "assigned";

export interface FetchEventDetailsPayload {
  client_id: number;
  event_id: number;
  user_id?: number;
}

export interface RegisterForEventPayload {
  program_id: number;
  client_id: number;
  ticket_type_id: number;

  registrant_type: EventRegistrantType;

  user_id?: number | null;
  contact_id?: number | null;

  registrant_name: string;
  mobile_number: string;
  email?: string | null;

  quantity?: number;
  remarks?: string | null;
}

export interface CreateEventPaymentOrderPayload {
  client_id: number;

  registration_id: number;

  amount: number;
  payable_amount?: number;

  payer_user_id?: number | null;
  payer_contact_id?: number | null;
  payer_client_id?: number | null;

  payer_name?: string | null;
  payer_mobile?: string | null;
  payer_email?: string | null;

  source_app?:
    | "club"
    | "club_web"
    | "club_mobile"
    | "public"
    | "public_web"
    | "public_mobile";

  source_platform?: "web" | "mobile";

  currency?: string;

  return_url?: string | null;
  notify_url?: string | null;
}

export interface CreateEventPaymentSessionPayload {
  order_id: number;
  return_url?: string;
  notify_url?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
}

export interface FetchEventPaymentStatusPayload {
  order_id: number;
}

interface EventState {
  event: any | null;

  registration: any | null;

  paymentOrder: any | null;

  paymentSession: any | null;

  paymentStatus: any | null;

  eventLoading: boolean;
  registrationLoading: boolean;
  paymentOrderLoading: boolean;
  paymentSessionLoading: boolean;
  paymentStatusLoading: boolean;

  error: any | null;
}

// ============================================================
// HELPERS
// ============================================================

const getErrorPayload = (
  error: any,
  fallback: string
) => {
  return (
    error?.response?.data || {
      success: false,
      status: false,
      message: fallback,
    }
  );
};

const getDateOnly = (
  value?: string | null
) => {
  if (!value) return null;

  const stringValue =
    String(value);

  if (stringValue.includes("T")) {
    return stringValue.split("T")[0];
  }

  return stringValue;
};

const normalizeEvent = (
  program: any
) => {
  if (!program) return null;

  const startDate =
    getDateOnly(
      program.start_date
    );

  const endDate =
    getDateOnly(
      program.end_date
    );

  return {
    ...program,

    event_id:
      program.event_id ||
      program.program_id,

    event_title:
      program.event_title ||
      program.program_title ||
      program.title ||
      "Program",

    event_description:
      program.event_description ||
      program.program_description ||
      program.description ||
      "",

    from_date_time:
      program.from_date_time ||
      (startDate
        ? `${startDate}T${
            program.start_time ||
            "00:00:00"
          }`
        : null),

    to_date_time:
      program.to_date_time ||
      (endDate
        ? `${endDate}T${
            program.end_time ||
            "23:59:59"
          }`
        : null),

    location:
      program.location ||
      program.venue_name ||
      program.venue ||
      program.venue_address ||
      program.address ||
      null,

    visibility:
      (
        program.visibility ||
        "public"
      )
        .toString()
        .toLowerCase(),

    ticket_types:
      Array.isArray(
        program.ticket_types
      )
        ? program.ticket_types
        : [],

    assignments:
      Array.isArray(
        program.assignments
      )
        ? program.assignments
        : [],

    documents:
      Array.isArray(
        program.documents
      )
        ? program.documents
        : [],

    is_member:
      Number(
        program.is_member ||
          0
      ),

    is_assigned:
      Number(
        program.is_assigned ||
          0
      ),

    is_registered:
      Number(
        program.is_registered ||
          0
      ),
  };
};

// ============================================================
// FETCH EVENT DETAILS
//
// Uses the user-visible programs endpoint so private/assigned
// visibility rules are enforced by backend.
// ============================================================

export const fetchEventDetails =
  createAsyncThunk(
    "event/fetchEventDetails",

    async (
      {
        client_id,
        event_id,
        user_id,
      }: FetchEventDetailsPayload,
      { rejectWithValue }
    ) => {
      try {
        const params =
          new URLSearchParams();

        params.append(
          "client_id",
          String(client_id)
        );

        if (
          user_id &&
          Number(user_id) > 0
        ) {
          params.append(
            "user_id",
            String(user_id)
          );
        }

        const res =
          await axios.get(
            `${API_BASE}/programs/user?${params.toString()}`
          );

        const sectionData =
          res.data?.data || {};

        if (
          Number(
            sectionData?.can_view_events ??
              1
          ) === 0
        ) {
          return rejectWithValue({
            success: false,
            status: false,
            code:
              "EVENTS_SECTION_PRIVATE",
            message:
              sectionData?.private_message ||
              "The events section is private.",
          });
        }

        const events =
          Array.isArray(
            sectionData?.events
          )
            ? sectionData.events
            : [];

        const found =
          events.find(
            (program: any) =>
              Number(
                program.program_id ||
                  program.event_id
              ) ===
              Number(event_id)
          ) || null;

        if (!found) {
          return rejectWithValue({
            success: false,
            status: false,
            code:
              "EVENT_NOT_FOUND_OR_NOT_VISIBLE",
            message:
              "Event not found or you do not have access to this event.",
          });
        }

        return normalizeEvent(
          found
        );
      } catch (error: any) {
        return rejectWithValue(
          getErrorPayload(
            error,
            "Failed to fetch event details"
          )
        );
      }
    }
  );

// ============================================================
// REGISTER FOR EVENT
//
// Backend decides:
// public   -> member/guest rules
// private  -> member only
// assigned -> assigned member/contact only
//
// Free ticket:
// registration_status = confirmed
// payment_status      = not_required
//
// Paid ticket:
// registration_status = pending
// payment_status      = pending
// ============================================================

export const registerForEvent =
  createAsyncThunk(
    "event/registerForEvent",

    async (
      payload: RegisterForEventPayload,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.post(
            `${API_BASE}/programs/${payload.program_id}/registrations`,
            {
              client_id:
                payload.client_id,

              ticket_type_id:
                payload.ticket_type_id,

              registrant_type:
                payload.registrant_type,

              user_id:
                payload.user_id ||
                null,

              contact_id:
                payload.contact_id ||
                null,

              registrant_name:
                payload.registrant_name,

              mobile_number:
                payload.mobile_number,

              email:
                payload.email ||
                null,

              quantity:
                payload.quantity ||
                1,

              remarks:
                payload.remarks ||
                null,
            }
          );

        return (
          res.data?.data ||
          null
        );
      } catch (error: any) {
        return rejectWithValue(
          getErrorPayload(
            error,
            "Failed to register for event"
          )
        );
      }
    }
  );

// ============================================================
// CREATE EVENT PAYMENT ORDER
//
// reference_type = program_registration
// reference_id   = registration_id
// transaction_type = event_registration
// ============================================================

export const createEventPaymentOrder =
  createAsyncThunk(
    "event/createEventPaymentOrder",

    async (
      payload: CreateEventPaymentOrderPayload,
      { rejectWithValue }
    ) => {
      try {
        const payableAmount =
          Number(
            payload.payable_amount ??
              payload.amount
          );

        const res =
          await axios.post(
            `${API_BASE}/billing-finance/payment-orders`,
            {
              client_id:
                payload.client_id,

              transaction_type:
                "event_registration",

              reference_type:
                "program_registration",

              reference_id:
                payload.registration_id,

              amount:
                Number(
                  payload.amount
                ),

              payable_amount:
                payableAmount,

              payer_user_id:
                payload.payer_user_id ||
                null,

              payer_contact_id:
                payload.payer_contact_id ||
                null,

              payer_client_id:
                payload.payer_client_id ||
                null,

              payer_name:
                payload.payer_name ||
                null,

              payer_mobile:
                payload.payer_mobile ||
                null,

              payer_email:
                payload.payer_email ||
                null,

              source_app:
                payload.source_app ||
                "public_web",

              source_platform:
                payload.source_platform ||
                "web",

              currency:
                payload.currency ||
                "INR",

              return_url:
                payload.return_url ||
                undefined,

              notify_url:
                payload.notify_url ||
                undefined,
            }
          );

        return (
          res.data?.data ||
          res.data ||
          null
        );
      } catch (error: any) {
        return rejectWithValue(
          getErrorPayload(
            error,
            "Failed to create event payment order"
          )
        );
      }
    }
  );

// ============================================================
// CREATE CASHFREE PAYMENT SESSION
// ============================================================

export const createEventPaymentSession =
  createAsyncThunk(
    "event/createEventPaymentSession",

    async (
      {
        order_id,
        ...body
      }: CreateEventPaymentSessionPayload,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.post(
            `${API_BASE}/billing-finance/payment-orders/${order_id}/session`,
            body
          );

        return (
          res.data?.data ||
          res.data ||
          null
        );
      } catch (error: any) {
        return rejectWithValue(
          getErrorPayload(
            error,
            "Failed to create payment session"
          )
        );
      }
    }
  );

// ============================================================
// GET PAYMENT STATUS
//
// When Cashfree returns PAID, backend finalizes:
// payment_status      = paid
// registration_status = confirmed
// ============================================================

export const fetchEventPaymentStatus =
  createAsyncThunk(
    "event/fetchEventPaymentStatus",

    async (
      {
        order_id,
      }: FetchEventPaymentStatusPayload,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.get(
            `${API_BASE}/billing-finance/payment-orders/${order_id}/status`
          );

        return (
          res.data?.data ||
          res.data ||
          null
        );
      } catch (error: any) {
        return rejectWithValue(
          getErrorPayload(
            error,
            "Failed to fetch payment status"
          )
        );
      }
    }
  );

// ============================================================
// INITIAL STATE
// ============================================================

const initialState: EventState = {
  event: null,

  registration: null,

  paymentOrder: null,

  paymentSession: null,

  paymentStatus: null,

  eventLoading: false,
  registrationLoading: false,
  paymentOrderLoading: false,
  paymentSessionLoading: false,
  paymentStatusLoading: false,

  error: null,
};

// ============================================================
// SLICE
// ============================================================

const eventSlice =
  createSlice({
    name: "event",

    initialState,

    reducers: {
      clearEventState: (
        state
      ) => {
        state.event = null;
        state.registration =
          null;
        state.paymentOrder =
          null;
        state.paymentSession =
          null;
        state.paymentStatus =
          null;
        state.error = null;
      },

      clearEventPayment: (
        state
      ) => {
        state.paymentOrder =
          null;
        state.paymentSession =
          null;
        state.paymentStatus =
          null;
        state.error = null;
      },

      clearEventError: (
        state
      ) => {
        state.error = null;
      },

      setEvent: (
        state,
        action: PayloadAction<any>
      ) => {
        state.event =
          normalizeEvent(
            action.payload
          );
      },
    },

    extraReducers: (
      builder
    ) => {
      builder

        // ====================================================
        // EVENT DETAILS
        // ====================================================

        .addCase(
          fetchEventDetails.pending,
          (state) => {
            state.eventLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          fetchEventDetails.fulfilled,
          (
            state,
            action
          ) => {
            state.eventLoading =
              false;

            state.event =
              action.payload;
          }
        )

        .addCase(
          fetchEventDetails.rejected,
          (
            state,
            action
          ) => {
            state.eventLoading =
              false;

            state.event = null;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // REGISTRATION
        // ====================================================

        .addCase(
          registerForEvent.pending,
          (state) => {
            state.registrationLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          registerForEvent.fulfilled,
          (
            state,
            action
          ) => {
            state.registrationLoading =
              false;

            state.registration =
              action.payload;

            if (
              state.event &&
              action.payload
            ) {
              state.event.is_registered =
                1;
            }
          }
        )

        .addCase(
          registerForEvent.rejected,
          (
            state,
            action
          ) => {
            state.registrationLoading =
              false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // PAYMENT ORDER
        // ====================================================

        .addCase(
          createEventPaymentOrder.pending,
          (state) => {
            state.paymentOrderLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          createEventPaymentOrder.fulfilled,
          (
            state,
            action
          ) => {
            state.paymentOrderLoading =
              false;

            state.paymentOrder =
              action.payload;
          }
        )

        .addCase(
          createEventPaymentOrder.rejected,
          (
            state,
            action
          ) => {
            state.paymentOrderLoading =
              false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // PAYMENT SESSION
        // ====================================================

        .addCase(
          createEventPaymentSession.pending,
          (state) => {
            state.paymentSessionLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          createEventPaymentSession.fulfilled,
          (
            state,
            action
          ) => {
            state.paymentSessionLoading =
              false;

            state.paymentSession =
              action.payload;
          }
        )

        .addCase(
          createEventPaymentSession.rejected,
          (
            state,
            action
          ) => {
            state.paymentSessionLoading =
              false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // PAYMENT STATUS
        // ====================================================

        .addCase(
          fetchEventPaymentStatus.pending,
          (state) => {
            state.paymentStatusLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          fetchEventPaymentStatus.fulfilled,
          (
            state,
            action
          ) => {
            state.paymentStatusLoading =
              false;

            state.paymentStatus =
              action.payload;

            const paymentStatus =
              String(
                action.payload
                  ?.registration_payment_status ||
                  action.payload
                    ?.payment_status ||
                  ""
              ).toLowerCase();

            const registrationStatus =
              String(
                action.payload
                  ?.registration_status ||
                  ""
              ).toLowerCase();

            if (
              state.registration &&
              (
                paymentStatus ===
                  "paid" ||
                registrationStatus ===
                  "confirmed"
              )
            ) {
              state.registration = {
                ...state.registration,

                payment_status:
                  paymentStatus ||
                  "paid",

                registration_status:
                  registrationStatus ||
                  "confirmed",

                payment_order_id:
                  action.payload
                    ?.order_id ||
                  state.registration
                    ?.payment_order_id ||
                  null,
              };
            }
          }
        )

        .addCase(
          fetchEventPaymentStatus.rejected,
          (
            state,
            action
          ) => {
            state.paymentStatusLoading =
              false;

            state.error =
              action.payload;
          }
        );
    },
  });

// ============================================================
// EXPORTS
// ============================================================

export const {
  clearEventState,
  clearEventPayment,
  clearEventError,
  setEvent,
} = eventSlice.actions;

export default eventSlice.reducer;
